import React, { useEffect, useState } from 'react';
import {
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import PhoneIcon from '@mui/icons-material/Phone';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import dayjs from 'dayjs';
import PrepTimeWarning from './PrepTimeWarning';
import EditableText from './EditableText';
import EmailBounceStateIndicator from '../../../Mail/EmailBounceStateIndicator';
import useEmailBounceState from '../../../../hooks/useEmailBounceState';

function formatDuration(durationMs) {
  const totalMinutesRaw = Number(durationMs) / 60000;
  if (!Number.isFinite(totalMinutesRaw) || totalMinutesRaw < 0) return '0 Minuten';

  const totalMinutes = Math.round(totalMinutesRaw);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours === 0) {
    return `${minutes} ${minutes === 1 ? 'Minute' : 'Minuten'}`;
  }

  if (minutes === 0) {
    return `${hours} ${hours === 1 ? 'Stunde' : 'Stunden'}`;
  }

  return `${hours} ${hours === 1 ? 'Stunde' : 'Stunden'} ${minutes} ${
    minutes === 1 ? 'Minute' : 'Minuten'
  }`;
}

// Lazy lookup of the per-address bounce state for the booking's email.
// Lives next to BookingInfoSection because there is no per-booking backend
// enrichment for the detail view (only list endpoints are decorated).
function BookingMailBounceIndicator({ email }) {
  const [state, setState] = useState(null);
  const { fetchByEmail } = useEmailBounceState();

  useEffect(() => {
    if (!email) {
      setState(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const s = await fetchByEmail(email);
        if (!cancelled) setState(s || null);
      } catch (_) {
        if (!cancelled) setState(null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email]);

  return <EmailBounceStateIndicator state={state} showSoftBounces />;
}

// Resolve the canonical list of tables assigned to a booking. Prefers the
// multi-table field and falls back to the legacy single tableNumber.
function resolveAssignedTables(booking) {
  if (Array.isArray(booking?.tableNumbers) && booking.tableNumbers.length > 0) {
    return booking.tableNumbers.map(Number).filter(n => Number.isFinite(n) && n > 0);
  }
  const single = Number(booking?.tableNumber);
  return Number.isFinite(single) && single > 0 ? [single] : [];
}

// Multi-table selector: one dropdown per assigned table, a remove button per row
// and a "+" button to add a further table. A table already picked in another row
// is not offered again. Selections persist immediately via onChange.
function TableSelector({ assignedTables, maxTableNumber, unusableTableSet, disabled, onChange }) {
  // '' represents an empty (not yet chosen) row so there is always a visible dropdown.
  const [rows, setRows] = useState(() => (assignedTables.length > 0 ? assignedTables : ['']));
  const assignedKey = assignedTables.join(',');

  // Re-sync local rows when the persisted assignment changes (e.g. after save).
  useEffect(() => {
    setRows(assignedTables.length > 0 ? assignedTables : ['']);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedKey]);

  const persist = async nextRows => {
    const numbers = nextRows.filter(v => v !== '' && v != null).map(Number);
    const unique = numbers.filter((n, index) => numbers.indexOf(n) === index);
    try {
      await onChange(unique);
    } catch (_) {
      // Revert the optimistic selection to the last persisted assignment on failure.
      setRows(assignedTables.length > 0 ? assignedTables : ['']);
    }
  };

  const buildOptions = currentValue => {
    const takenByOtherRows = new Set(
      rows.filter(v => v !== '' && v !== currentValue).map(Number)
    );
    return Array.from({ length: maxTableNumber }, (_, index) => index + 1).filter(
      number =>
        (!unusableTableSet.has(number) || number === currentValue) &&
        !takenByOtherRows.has(number)
    );
  };

  const handleRowChange = (index, value) => {
    const next = [...rows];
    next[index] = value === '' ? '' : Number(value);
    setRows(next);
    persist(next);
  };

  const handleRemoveRow = index => {
    const filtered = rows.filter((_, i) => i !== index);
    const next = filtered.length > 0 ? filtered : [''];
    setRows(next);
    persist(next);
  };

  const handleAddRow = () => {
    setRows(prev => [...prev, '']);
  };

  const selectedCount = rows.filter(v => v !== '' && v != null).length;
  const hasMultipleRows = rows.length > 1;
  // Only allow adding another dropdown when every row is filled and tables remain.
  const canAddMore =
    !disabled && selectedCount === rows.length && selectedCount < maxTableNumber;

  return (
    <Box sx={{ mt: 1 }}>
      <Stack direction='row' alignItems='center' spacing={0.5} sx={{ mb: 0.75 }}>
        <TableRestaurantIcon fontSize='small' color='action' />
        <Typography variant='body2' sx={{ fontWeight: 600 }}>
          {hasMultipleRows ? 'Tische' : 'Tisch'}
        </Typography>
      </Stack>
      <Stack spacing={1}>
        {rows.map((value, index) => {
          const options = buildOptions(value);
          const label = hasMultipleRows ? `Tisch ${index + 1}` : 'Tisch';
          return (
            <Stack key={index} direction='row' spacing={0.5} alignItems='center'>
              <FormControl
                size='small'
                sx={{ minWidth: 180, maxWidth: 260, flex: 1 }}
                disabled={disabled}
              >
                <InputLabel id={`booking-table-${index}-label`}>{label}</InputLabel>
                <Select
                  labelId={`booking-table-${index}-label`}
                  label={label}
                  value={value === '' ? '' : value}
                  onChange={event => handleRowChange(index, event.target.value)}
                >
                  <MenuItem value=''>
                    <em>Kein Tisch</em>
                  </MenuItem>
                  {options.map(number => (
                    <MenuItem key={number} value={number}>
                      Tisch {number}
                      {unusableTableSet.has(number) ? ' (defekt)' : ''}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {hasMultipleRows && (
                <Tooltip title='Tisch entfernen'>
                  <span>
                    <IconButton
                      size='small'
                      onClick={() => handleRemoveRow(index)}
                      disabled={disabled}
                    >
                      <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
          );
        })}
      </Stack>
      <Button
        size='small'
        startIcon={<AddIcon />}
        onClick={handleAddRow}
        disabled={!canAddMore}
        sx={{ mt: 0.5, textTransform: 'none' }}
      >
        Weiteren Tisch hinzufügen
      </Button>
    </Box>
  );
}

function BookingInfoSection({
  booking,
  prepTimeMinutes,
  prepTimeWarnings,
  isDev,
  isToday,
  isEditable,
  hasFullUpdateRights,
  hasEmailUpdateRights = false,
  hasUpdateRights,
  lockPaymentBtn,
  currentlyEditingField,
  onFieldEditStart,
  onFieldEditEnd,
  onUpdateMail,
  onUpdatePhone,
  onUpdateCompanyName,
  onUpdateTableNumbers,
  onSetPaymentModalOpen,
  canMarkBookingAsPaid = false,
  onResendConfirmation,
  isResendingConfirmation = false,
  onInitiateCall,
  canShowCallButton = false,
  hasActiveCallAgent = false,
  hasOverrideCapacityRights = false,
  onOpenCapacityOverrideDialog,
}) {
  const hasPayableAmount = parseFloat(booking?.totalAmount || 0) > 0;
  const isEffectivelyPaid = booking.fullyPaid || !hasPayableAmount;
  // A payment reference settled against an invoice without an online payment
  // means the booking was paid by invoice (bank transfer confirmed by staff)
  const isPaidViaInvoice =
    booking.fullyPaid &&
    (booking.lineItems || []).some(li =>
      (li.paymentReferences || []).some(
        ref => ref.isPaid && ref.relatedInvoiceId && !ref.relatedPaymentId
      )
    );
  const bookingNumberDisplay = (() => {
    const v = booking?.bookingNumber;
    if (v == null) return '—';
    if (typeof v === 'object') {
      try {
        if (Object.keys(v).length === 0) return '—';
      } catch (_) {}
    }
    return String(v);
  })();
  const bookingIdDisplay = (() => {
    const v = booking?._id;
    if (v == null) return '';
    if (typeof v === 'object') {
      try {
        if (Object.keys(v).length === 0) return '';
      } catch (_) {}
    }
    return String(v);
  })();

  const participantCountRaw = Number(booking?.participantCount || 0);
  const participantCount =
    Number.isFinite(participantCountRaw) && participantCountRaw > 0 ? participantCountRaw : 0;
  // People still physically present after any spontaneous reductions ("Spontan weniger Personen").
  // Mirrors the backend (Variant A): the charged participantCount stays untouched so the full price
  // is guaranteed; the number actually present is derived as participantCount minus the sum of the
  // active (non-canceled) reductions. Surfacing this makes clear that the reduction took effect even
  // though the billed count does not change.
  const spontaneousReducedCount = (
    Array.isArray(booking?.spontaneousReductions) ? booking.spontaneousReductions : []
  )
    .filter(r => r && r.status !== 'canceled')
    .reduce((sum, r) => sum + (Number(r.count) || 0), 0);
  const hasSpontaneousReduction = spontaneousReducedCount > 0;
  const presentParticipantCount = Math.max(0, participantCount - spontaneousReducedCount);
  const participantLaneDensityRaw = Number(
    booking?.participantLaneDensity ?? booking?.entertainmentId?.participantLaneDensity ?? 0
  );
  const participantLaneDensity =
    Number.isFinite(participantLaneDensityRaw) && participantLaneDensityRaw > 0
      ? participantLaneDensityRaw
      : null;
  const bookedLaneCountRaw = Number(booking?.laneCount || 0);
  const bookedLaneCount =
    Number.isFinite(bookedLaneCountRaw) && bookedLaneCountRaw > 0 ? bookedLaneCountRaw : null;
  const computedLaneCount =
    participantLaneDensity && participantCount > 0
      ? Math.max(1, Math.ceil(participantCount / participantLaneDensity))
      : null;
  const laneCountDisplay = bookedLaneCount ?? computedLaneCount;
  const showLaneCount = participantLaneDensity && laneCountDisplay != null;
  const durationDisplay = formatDuration(booking?.eventDurationMS);
  const canEditEmail = hasFullUpdateRights || hasEmailUpdateRights;
  const entertainmentDisplay =
    booking?.entertainmentId?.longName ||
    booking?.entertainmentId?.entertainmentName ||
    booking?.entertainment ||
    '—';
  const maxTableNumberRaw = Number(booking?.entertainmentId?.maxTableNumber || 0);
  const maxTableNumber =
    Number.isFinite(maxTableNumberRaw) && maxTableNumberRaw > 0 ? maxTableNumberRaw : 0;
  const unusableTableSet = new Set(
    (Array.isArray(booking?.entertainmentId?.unusableTables)
      ? booking.entertainmentId.unusableTables
      : []
    ).map(Number)
  );
  const assignedTables = resolveAssignedTables(booking);

  const hasCapacityOverride =
    (typeof booking?.capacityParticipantCount === 'number' &&
      booking.capacityParticipantCount >= 0) ||
    (typeof booking?.capacityLaneCount === 'number' && booking.capacityLaneCount >= 0);

  const capacityOverrideParts = [];
  if (typeof booking?.capacityParticipantCount === 'number' && booking.capacityParticipantCount >= 0) {
    capacityOverrideParts.push(`${booking.capacityParticipantCount} Teilnehmer`);
  }
  if (typeof booking?.capacityLaneCount === 'number' && booking.capacityLaneCount >= 0) {
    capacityOverrideParts.push(`${booking.capacityLaneCount} Bahnen`);
  }

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Box sx={{ flex: 1 }}>
        <PrepTimeWarning
          prepTimeMinutes={prepTimeMinutes}
          prepTimeWarnings={prepTimeWarnings}
          booking={booking}
        />

        <Stack spacing={0.5} mb={2}>
          <Typography variant='body1'>
            <b>Buchungsnummer:</b> {bookingNumberDisplay}
          </Typography>
          <Typography variant='body1'>
            <b>Entertainment:</b> {entertainmentDisplay}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditableText
              value={booking.mail}
              onSave={onUpdateMail}
              label='E-Mail'
              disabled={
                !isEditable ||
                !canEditEmail ||
                (currentlyEditingField && currentlyEditingField !== 'mail')
              }
              sx={{ mb: 0.5 }}
              onEditStart={() => onFieldEditStart('mail')}
              onEditEnd={onFieldEditEnd}
            />
            <BookingMailBounceIndicator email={booking.mail} />
            <Tooltip title={'Bestätigung erneut senden'}>
              <span>
                <IconButton
                  size='small'
                  color='primary'
                  onClick={onResendConfirmation}
                  disabled={!isEditable || isResendingConfirmation || !booking.mail}
                >
                  {isResendingConfirmation ? <CircularProgress size={18} /> : <SendIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          {booking.companyName !== undefined && (
            <EditableText
              value={booking.companyName || ''}
              onSave={onUpdateCompanyName}
              label='Firma'
              disabled={
                !isEditable ||
                !hasFullUpdateRights ||
                (currentlyEditingField && currentlyEditingField !== 'companyName')
              }
              sx={{ mb: 0.5 }}
              onEditStart={() => onFieldEditStart('companyName')}
              onEditEnd={onFieldEditEnd}
            />
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EditableText
              value={booking.phone}
              onSave={onUpdatePhone}
              label='Telefon'
              disabled={
                !isEditable ||
                !hasFullUpdateRights ||
                (currentlyEditingField && currentlyEditingField !== 'phone')
              }
              onEditStart={() => onFieldEditStart('phone')}
              onEditEnd={onFieldEditEnd}
            />
            {canShowCallButton && (
              <Tooltip title='Anruf starten'>
                <span>
                  <IconButton
                    size='small'
                    color='primary'
                    onClick={onInitiateCall}
                    disabled={!hasActiveCallAgent || !booking.phone}
                  >
                    <PhoneIcon />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
          <Typography variant='body1'>
            <b>Datum:</b> {dayjs(booking.startTime).format('DD.MM.YYYY')}
          </Typography>
          <Typography variant='body1'>
            <b>Zeitraum:</b> {dayjs(booking.startTime).format('HH:mm')} -{' '}
            {dayjs(booking.endTime).format('HH:mm')} ({durationDisplay})
          </Typography>
          {booking.responseDeadline && (
            <Typography variant='body1'>
              <b>Rückmeldung bis:</b>{' '}
              {dayjs(booking.responseDeadline).format('DD.MM.YYYY HH:mm')} Uhr
            </Typography>
          )}
          <Typography variant='body1'>
            <b>Teilnehmer:</b>{' '}
            {hasSpontaneousReduction ? (
              <>
                {presentParticipantCount}{' '}
                <Typography component='span' variant='body2' sx={{ color: 'text.secondary' }}>
                  (von {participantCount} gebucht · {spontaneousReducedCount} spontan reduziert)
                </Typography>
              </>
            ) : (
              booking.participantCount
            )}
            {showLaneCount && (
              <>
                {' '}
                - <b>Bahnen:</b> {computedLaneCount ?? laneCountDisplay}
                {bookedLaneCount != null &&
                  computedLaneCount != null &&
                  bookedLaneCount !== computedLaneCount && <> (gebucht: {bookedLaneCount})</>}
              </>
            )}
          </Typography>
          {hasCapacityOverride && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant='body1'>
                <b>Kapazitätsübersteuerung:</b> {capacityOverrideParts.join(', ')}
              </Typography>
              {hasOverrideCapacityRights && (
                <Tooltip title='Kapazitätsübersteuerung bearbeiten'>
                  <IconButton
                    size='small'
                    color='primary'
                    onClick={onOpenCapacityOverrideDialog}
                    disabled={!isEditable}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
          <Typography variant='body1'>
            <b>Zahlstatus:</b>{' '}
            {isEffectivelyPaid ? (
              <>
                <CheckCircleIcon color='success' sx={{ verticalAlign: 'middle' }} />
                {isPaidViaInvoice && (
                  <Typography
                    component='span'
                    variant='body2'
                    sx={{ color: 'text.secondary', ml: 0.5 }}
                  >
                    (bezahlt per Rechnung)
                  </Typography>
                )}
              </>
            ) : (
              <>
                {lockPaymentBtn ? (
                  <CircularProgress size={20} sx={{ verticalAlign: 'middle', ml: 1 }} />
                ) : (
                  <CancelIcon color='error' sx={{ verticalAlign: 'middle' }} />
                )}
                {canMarkBookingAsPaid && hasPayableAmount && (
                  <Button
                    variant='contained'
                    color='success'
                    size='small'
                    sx={{
                      ml: 1,
                      mt: { xs: 1, sm: 0 },
                      alignSelf: { xs: 'center', sm: 'auto' },
                      '&.Mui-disabled': {
                        backgroundColor: 'rgba(76, 175, 80, 0.3)',
                        color: 'rgba(0, 0, 0, 0.5)',
                      },
                    }}
                    onClick={() => onSetPaymentModalOpen(true)}
                    disabled={lockPaymentBtn || !hasUpdateRights}
                  >
                    Jetzt Bezahlen
                  </Button>
                )}
              </>
            )}
          </Typography>
          {maxTableNumber > 0 && (
            <TableSelector
              assignedTables={assignedTables}
              maxTableNumber={maxTableNumber}
              unusableTableSet={unusableTableSet}
              disabled={!isEditable || !hasUpdateRights}
              onChange={onUpdateTableNumbers}
            />
          )}
          {isDev && (
            <Typography variant='body1'>
              <b>ID:</b> {bookingIdDisplay}
            </Typography>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

export default BookingInfoSection;
